import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useTheme from '@kufox/mui/hooks/useTheme';
import px2rem from '@kufox/mui/utils/px2rem';
import { css } from '@kufox/mui/emotion';
import { queryPersistence } from '@kc/gbiz-base/lib/QueryPersistence';
import useMediaQuery from '@kufox/mui/hooks/useMediaQuery';
import CmsComs from '../CmsComs';
import { namespace } from '../CmsComs/model';
import MarketsAmount from './MarketsAmount';
import ServerTime from './ServerTime';
import LoadStyle from '../CmsComs/LoadStyle';
import ArrowDownIcon from '../static/arrow-down.png';
import { changLangToPath } from '../common/tools';

const useStyle = ({ color, breakpoints }) => {
  return {
    cFooter: css`
      transition: all 0.3s ease-in-out;
      padding: 0 24px;
      @media screen and (max-width: 768px) {
        padding: 0 12px;
      }
      & a {
        text-decoration: none !important;
        width: fit-content;
      }
      & .footerMain {
        max-width: 1200px;
        margin: 0 auto;
        padding-top: 48px;
        & .logo {
          display: block;
          height: 36px;
          margin-bottom: 40px;
        }
        & .divider {
          margin: 100px 0 4px;
          height: 1px;
          border: none;
          border-bottom: 1px solid;
          border-bottom-color: ${color.divider};
        }
        & .copyright {
          padding: 22px 0px;
          align-items: center;
          display: flex;
          justify-content: space-between;
          @media screen and (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
          }
          .new-copyright {
            font-size: 12px;
            color: ${color.text60};
          }
          .servertime {
            display: flex;
            @media screen and (max-width: 768px) {
              flex-direction: column;
              align-items: flex-start;
            }
          }
        }
      }
    `,
    cmslinks: css`
      .newFooterLinks {
        justify-content: space-between;
        &:after {
          width: 50%;
          @media screen and (max-width: 768px) {
            width: 0;
          }
          content: '';
        }
        .newFooterLinkGroup {
          width: 20%;
          min-width: 240px;
          &.association {
            width: 20%;
          }
          @media screen and (max-width: 1024px) {
            margin-right: 30px;
          }
          .newFooterHover {
            .newFooterHoverMenu {
              a {
                color: ${color.text60};
                &:hover {
                  background: ${color.cover4};
                }
              }
              &:after {
                border-top-color: ${color.base};
              }
              background: ${color.base};
              &.newFooterSDK {
                bottom: 32px;
                @media screen and (max-width: 768px) {
                  left: 120px;
                  &:after {
                    left: 20px;
                  }
                }
              }
              &.newFooterFacebook {
                @media screen and (max-width: 1024px) {
                  left: 90px;
                  &:after {
                    left: 50px;
                  }
                }
                @media screen and (max-width: 768px) {
                  &:after {
                    left: 20px;
                  }
                }
              }
              &.newFooterTwitter {
                @media screen and (max-width: 1024px) {
                  left: 70px;
                  &:after {
                    left: 70px;
                  }
                }
              }
              &.newFooterReddit {
                @media screen and (max-width: 1024px) {
                  left: 60px;
                  &:after {
                    left: 74px;
                  }
                }
              }
            }
          }
          .newFooterLinkGroupTitle {
            color: ${color.text};
            & ~ a,
            & ~ div {
              color: ${color.text60};
            }
          }
          ${breakpoints.down('md')} {
            &:last-child {
              margin-right: auto;
              width: 100%;
            }
            width: 100%;
            margin-right: 0;
            .newFooterHover {
              .newFooterHoverMenu {
                &.newFooterFacebook {
                  left: 120px;
                  bottom: 38px;
                }
              }
            }
            .newFooterLinkGroupTitle {
              font-size: 18px;
            }
            &:not(:last-child) {
              .newFooterLinkGroupTitle {
                position: relative;
                cursor: pointer;
                & ~ a,
                & ~ div {
                  display: none;
                }
              }
              .newFooterLinkGroupTitle:after {
                position: absolute;
                top: 0;
                right: 0;
                content: ' ';
                width: 24px;
                height: 24px;
                margin-top: 22px;
                background-image: url(${ArrowDownIcon});
                background-repeat: no-repeat;
                background-size: 100% 100%;
                transition: all 0.3s ease-in-out;
              }
            }
          }
        }
      }
    `,
    after: css`
      &:after {
        position: absolute;
        top: 0;
        right: 0;
        content: ' ';
        width: ${px2rem(24)};
        height: ${px2rem(24)};
        margin-top: ${px2rem(22)};
        background-image: url(${ArrowDownIcon});
        background-repeat: no-repeat;
        background-size: 100% 100%;
        transition: all 0.3s ease-in-out;
        transform: rotate(180deg);
        ${breakpoints.up('md')} {
          display: none;
        }
      }
    `,
  };
};

export default function Footer(props) {
  const {
    styleConfig = {},
    currentLang,
    KUCOIN_HOST, // kucoin主站地址
    TRADE_HOST, // 交易地址
    DOCS_HOST, // 文档地址
    KUMEX_HOST, // kumex地址
    SANDBOX_HOST, // 沙盒地址
    POOLX_HOST,
    LANDING_HOST, // 流量落地页地址
    TRADING_BOT_HOST, // 交易机器人
  } = props;
  const theme = useTheme();
  const styles = useStyle({ color: theme.colors, breakpoints: theme.breakpoints });
  const isMd = useMediaQuery('(min-width:768.05px)');
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const hostConfig = {
    KUCOIN_HOST,
    TRADE_HOST,
    DOCS_HOST,
    KUMEX_HOST,
    SANDBOX_HOST,
    POOLX_HOST,
    LANDING_HOST,
    TRADING_BOT_HOST,
  };
  useEffect(() => {
    dispatch({
      type: `${namespace}/fetch`,
      payload: {
        currentLang,
        hostConfig,
        componentsToload: ['cms.common'],
      },
    });
  }, [currentLang]);

  const handleTitleClick = useCallback(
    (e) => {
      if (isMd) return;
      const targetClassName = e.target.className;
      if (targetClassName.includes('newFooterLinkGroupTitle')) {
        if (e.target.parentNode.className.includes('association')) {
          return;
        }
        if (targetClassName.includes(styles.after)) {
          e.target.className = e.target.className.replace(styles.after, '');
        } else {
          e.target.className = e.target.className + ` ${styles.after}`;
        }
        const parent = e.target.parentNode;
        parent.childNodes.forEach((node, idx) => {
          if (node.nodeType === 1 && idx !== 0) {
            const display = node.style.display;
            node.style.display = display === 'none' || display === '' ? 'block' : 'none';
          }
        });
      }
    },
    [isMd],
  );

  useEffect(() => {
    const nodes = document.querySelectorAll('.newFooterLinkGroupTitle');
    nodes.forEach((node, idx) => {
      if (idx !== nodes.length - 1) {
        if (node.className.includes(styles.after)) {
          node.className = node.className.replace(styles.after, '');
        }
        const sibs = node.parentNode.childNodes;
        sibs.forEach((sib, index) => {
          if (sib.nodeType === 1 && index !== 0) {
            if (isMd) {
              sib.style.display = 'block';
            } else {
              sib.style.display = 'none';
            }
          }
        });
      }
    });
  }, [isMd]);

  return (
    <>
      <LoadStyle run={['cms.common']} lang={currentLang} />
      <div css={styles.cFooter} style={{ ...styleConfig }}>
        <div className="footerMain">
          <a
            href={changLangToPath(currentLang, queryPersistence.formatUrlWithStore(KUCOIN_HOST))}
            className="logo"
          >
            <CmsComs.Load run="com.newheader.logo" />
          </a>
          <div css={styles.cmslinks} onClick={handleTitleClick}>
            <CmsComs.Load run="com.newFooter.links.new" combine lang={currentLang} />
          </div>
          <div className="divider" />
          <div className="copyright">
            <div className="new-copyright">
              CopyRight © 2017 - {currentYear} KuCoin.com. All Rights Reserved.
            </div>
            <div className="servertime">
              <ServerTime {...props} />
              <MarketsAmount {...props} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
